/**
 * Migration Service
 *
 * Handles data migration between localStorage and Supabase Cloud
 *
 * Features:
 * - Migrate local data to cloud (when user enables sync)
 * - Export cloud data to local (for backup or offline use)
 * - Conflict resolution (local vs cloud)
 * - Progress tracking
 */

import { supabase } from '../lib/supabase/client';
import { StorageService } from './storage';

export interface MigrationProgress {
  stage: string;
  current: number;
  total: number;
  percentage: number;
}

export interface MigrationResult {
  success: boolean;
  itemsMigrated: number;
  errors: string[];
  duration: number;
}

/**
 * Data comparison result for conflict resolution
 */
export interface DataComparison {
  hasConflict: boolean;
  local: {
    tokensCount: number;
    positionsCount: number;
    tokens: Array<{ id: string; name: string; positionsCount: number }>;
  };
  cloud: {
    tokensCount: number;
    positionsCount: number;
    tokens: Array<{ id: string; name: string; positionsCount: number }>;
  };
  delta: {
    tokensOnlyLocal: string[];      // coin_ids only in local
    tokensOnlyCloud: string[];      // coin_ids only in cloud
    tokensInBoth: string[];         // coin_ids in both
    positionsOnlyLocal: number;     // count of positions only in local
    positionsOnlyCloud: number;     // count of positions only in cloud
  };
}

class MigrationService {
  // Lock to prevent concurrent migrations
  private isMigrating = false;

  /**
   * Migrate localStorage data to Supabase Cloud
   *
   * @param userId - User ID to associate data with
   * @param onProgress - Optional progress callback
   */
  async migrateToCloud(
    userId: string,
    onProgress?: (progress: MigrationProgress) => void
  ): Promise<MigrationResult> {
    // Prevent concurrent migrations
    if (this.isMigrating) {
      console.log('⚠️ Migration already in progress, skipping...');
      return {
        success: true,
        itemsMigrated: 0,
        errors: ['Migration already in progress'],
        duration: 0,
      };
    }

    this.isMigrating = true;
    const startTime = Date.now();
    const errors: string[] = [];
    let itemsMigrated = 0;

    try {
      console.log('🚀 Starting migration to cloud...');

      // Step 1: Get or create default portfolio
      // IMPORTANT: Use the existing default portfolio created by trigger!
      if (onProgress) {
        onProgress({
          stage: 'Préparation du portfolio...',
          current: 1,
          total: 5,
          percentage: 20,
        });
      }

      // First, check if user already has a default portfolio (created by trigger)
      const { data: existingPortfolios } = await supabase
        .from('portfolios')
        .select('id, name, is_default')
        .eq('user_id', userId)
        .eq('is_archived', false)
        .order('is_default', { ascending: false });

      let defaultPortfolioId: string;

      if (existingPortfolios && existingPortfolios.length > 0) {
        // Use existing default portfolio (created by trigger on signup)
        const defaultPortfolio = existingPortfolios.find(p => p.is_default) || existingPortfolios[0];
        defaultPortfolioId = defaultPortfolio.id;
        console.log('✅ Using existing portfolio:', defaultPortfolio.name, defaultPortfolioId);
      } else {
        // No portfolio exists, create one (shouldn't happen if trigger works)
        console.log('⚠️ No portfolio found, creating default...');
        const { data: newPortfolio, error: createError } = await supabase
          .from('portfolios')
          .insert({
            user_id: userId,
            name: 'Mon Portfolio Principal',
            description: 'Portfolio par défaut',
            is_default: true,
          })
          .select()
          .single();

        if (createError) {
          errors.push(`Portfolio creation: ${createError.message}`);
          throw new Error('Failed to create portfolio');
        }
        defaultPortfolioId = newPortfolio.id;
        itemsMigrated += 1;
      }

      // Step 2: Migrate tokens from PortfolioManager
      if (onProgress) {
        onProgress({
          stage: 'Migration des tokens...',
          current: 2,
          total: 5,
          percentage: 40,
        });
      }

      // Get tokens from PortfolioManager format
      // IMPORTANT: PortfolioManager stores ALL portfolios in 'portfolios' key as an array
      // Each portfolio object has a 'tokens' array with positions inside
      const localPortfolios = StorageService.get<any[]>('portfolios', []);
      let tokensToMigrate: any[] = [];

      // Extract tokens directly from portfolio objects (NOT from portfolio_${id} keys!)
      for (const lp of localPortfolios) {
        // Tokens are stored directly in the portfolio object
        if (lp.tokens && lp.tokens.length > 0) {
          console.log(`📦 Found ${lp.tokens.length} tokens in portfolio "${lp.name}"`);
          tokensToMigrate = [...tokensToMigrate, ...lp.tokens];
        }
      }

      // Fallback: also check old 'tokens' key
      const oldTokens = StorageService.get('tokens', []);
      if (oldTokens.length > 0 && tokensToMigrate.length === 0) {
        console.log(`📦 Found ${oldTokens.length} tokens in legacy 'tokens' key`);
        tokensToMigrate = oldTokens;
      }

      console.log(`📦 Total tokens to migrate: ${tokensToMigrate.length}`);

      // Get existing tokens in cloud to avoid duplicates
      const { data: existingCloudTokens } = await supabase
        .from('portfolio_tokens')
        .select('coin_id')
        .eq('user_id', userId);

      const existingCoinIds = new Set((existingCloudTokens || []).map(t => t.coin_id));

      if (tokensToMigrate.length > 0) {
        const tokensToInsert = tokensToMigrate
          .filter((t: any) => !existingCoinIds.has(t.id)) // Skip duplicates
          .map((t: any) => ({
            user_id: userId,
            portfolio_id: defaultPortfolioId,
            coin_id: t.id,
            symbol: t.displayName || t.symbol || t.id.toUpperCase(),
            display_name: t.displayName || t.name || t.symbol || t.id,
            target_price: t.targetPrice?.toString() || null,
            notes: t.note || null,
            is_archived: false,
          }));

        if (tokensToInsert.length > 0) {
          console.log(`📤 Inserting ${tokensToInsert.length} new tokens...`);
          const { error } = await supabase
            .from('portfolio_tokens')
            .insert(tokensToInsert);

          if (error) {
            errors.push(`Tokens: ${error.message}`);
            console.error('Token insert error:', error);
          } else {
            itemsMigrated += tokensToInsert.length;
          }
        } else {
          console.log('ℹ️ All tokens already exist in cloud');
        }
      }

      // Step 3: Migrate positions
      if (onProgress) {
        onProgress({
          stage: 'Migration des positions...',
          current: 3,
          total: 5,
          percentage: 60,
        });
      }

      // Get ALL tokens from cloud (including just-migrated ones)
      const { data: allCloudTokens } = await supabase
        .from('portfolio_tokens')
        .select('id, coin_id, portfolio_id')
        .eq('user_id', userId);

      // Extract positions from PortfolioManager structure (inside tokens)
      // IMPORTANT: Tokens are stored directly in portfolio objects, not in portfolio_${id} keys
      let positionsToMigrate: any[] = [];
      for (const lp of localPortfolios) {
        // Tokens are directly in the portfolio object
        if (lp.tokens && Array.isArray(lp.tokens)) {
          for (const token of lp.tokens) {
            if (token.positions && token.positions.length > 0) {
              console.log(`📦 Found ${token.positions.length} positions for token ${token.id}`);
              // Add tokenId to each position for mapping
              const positionsWithTokenId = token.positions.map((p: any) => ({
                ...p,
                tokenId: token.id,
              }));
              positionsToMigrate = [...positionsToMigrate, ...positionsWithTokenId];
            }
          }
        }
      }

      // Fallback: also check old 'positions' key
      const oldPositions = StorageService.get('positions', []);
      if (oldPositions.length > 0 && positionsToMigrate.length === 0) {
        console.log(`📦 Found ${oldPositions.length} positions in legacy 'positions' key`);
        positionsToMigrate = oldPositions;
      }

      console.log(`📦 Total positions to migrate: ${positionsToMigrate.length}`);

      if (positionsToMigrate.length > 0 && allCloudTokens && allCloudTokens.length > 0) {
        const skippedPositions: string[] = [];
        const positionsToInsert = positionsToMigrate
          .map((p: any) => {
            const cloudToken = allCloudTokens.find((t) => t.coin_id === p.tokenId);
            if (!cloudToken) {
              console.warn(`⚠️ Token not found in cloud for position: ${p.tokenId}`);
              skippedPositions.push(p.tokenId);
              return null;
            }

            return {
              user_id: userId,
              portfolio_id: cloudToken.portfolio_id,
              token_id: cloudToken.id,
              type: p.type,
              date: p.date,
              quantity: p.quantity?.toString() || '0',
              price: p.price?.toString() || '0',
              note: p.note || null,
              source: 'migration',
              exchange: p.exchange || null,
              fee: p.fee?.toString() || null,
              fee_currency: p.fee_currency || null,
              // Swap tracking
              is_swap: p.is_swap ?? null,
              swap_chain_id: p.swap_chain_id ?? null,
              original_cost_basis: p.original_cost_basis?.toString() ?? null,
              // Mining
              mining_pool: p.mining_pool ?? null,
              mining_hashrate: p.mining_hashrate?.toString() ?? null,
              // Staking
              staking_apy: p.staking_apy?.toString() ?? null,
              staking_duration: p.staking_duration ?? null,
              staking_cooldown: p.staking_cooldown ?? null,
              staking_platform: p.staking_platform ?? null,
              staking_end_date: p.staking_end_date ?? null,
              staking_is_locked: p.staking_is_locked ?? null,
            };
          })
          .filter((p) => p !== null);

        // Report skipped positions
        if (skippedPositions.length > 0) {
          const uniqueSkipped = [...new Set(skippedPositions)];
          errors.push(`Positions skipped (token not found): ${uniqueSkipped.join(', ')}`);
        }

        if (positionsToInsert.length > 0) {
          console.log(`📤 Inserting ${positionsToInsert.length} positions...`);
          const { error } = await supabase
            .from('portfolio_positions')
            .insert(positionsToInsert);

          if (error) {
            errors.push(`Positions: ${error.message}`);
            console.error('Position insert error:', error);
          } else {
            itemsMigrated += positionsToInsert.length;
          }
        }
      }

      // Step 4: Migrate price alerts (if any)
      if (onProgress) {
        onProgress({
          stage: 'Migrating alerts...',
          current: 4,
          total: 5,
          percentage: 80,
        });
      }

      const alerts = StorageService.get('price_alerts', []);
      if (alerts.length > 0) {
        const alertsToInsert = alerts.map((a: any) => ({
          user_id: userId,
          coin_id: a.coinId,
          symbol: a.symbol,
          coin_name: a.name || null,
          alert_type: a.type || 'price_above',
          target_value: a.targetPrice,
          current_price: a.currentPrice || null,
          is_active: a.isActive !== false,
          notify_email: false,
          notify_push: true,
          repeat_notification: false,
          note: null,
        }));

        const { error } = await supabase
          .from('portfolio_price_alerts')
          .insert(alertsToInsert);

        if (error) {
          errors.push(`Alerts: ${error.message}`);
        } else {
          itemsMigrated += alerts.length;
        }
      }

      // Step 5: Complete
      if (onProgress) {
        onProgress({
          stage: 'Migration complete!',
          current: 5,
          total: 5,
          percentage: 100,
        });
      }

      const duration = Date.now() - startTime;

      console.log(`✅ Migration completed in ${duration}ms`);
      console.log(`📦 Items migrated: ${itemsMigrated}`);

      if (errors.length > 0) {
        console.warn('⚠️ Migration completed with errors:', errors);
      }

      return {
        success: errors.length === 0,
        itemsMigrated,
        errors,
        duration,
      };
    } catch (error) {
      console.error('❌ Migration failed:', error);

      return {
        success: false,
        itemsMigrated,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        duration: Date.now() - startTime,
      };
    } finally {
      // Always release the lock
      this.isMigrating = false;
    }
  }

  /**
   * Export cloud data to localStorage (for backup or offline use)
   *
   * @param userId - User ID
   */
  async exportToLocal(userId: string): Promise<MigrationResult> {
    const startTime = Date.now();
    const errors: string[] = [];
    let itemsExported = 0;

    try {
      console.log('💾 Exporting cloud data to local...');

      // Fetch portfolios
      const { data: portfolios, error: portfoliosError } = await supabase
        .from('portfolios')
        .select('*')
        .eq('user_id', userId)
        .eq('is_archived', false);

      if (portfoliosError) {
        errors.push(`Portfolios: ${portfoliosError.message}`);
      } else if (portfolios) {
        StorageService.set('portfolios', portfolios);
        itemsExported += portfolios.length;
      }

      // Fetch tokens
      const { data: tokens, error: tokensError } = await supabase
        .from('portfolio_tokens')
        .select('*')
        .eq('user_id', userId)
        .eq('is_archived', false);

      if (tokensError) {
        errors.push(`Tokens: ${tokensError.message}`);
      } else if (tokens) {
        StorageService.set('tokens', tokens);
        itemsExported += tokens.length;
      }

      // Fetch positions
      const { data: positions, error: positionsError } = await supabase
        .from('portfolio_positions')
        .select('*')
        .eq('user_id', userId);

      if (positionsError) {
        errors.push(`Positions: ${positionsError.message}`);
      } else if (positions) {
        StorageService.set('positions', positions);
        itemsExported += positions.length;
      }

      // Fetch alerts
      const { data: alerts, error: alertsError } = await supabase
        .from('portfolio_price_alerts')
        .select('*')
        .eq('user_id', userId);

      if (alertsError) {
        errors.push(`Alerts: ${alertsError.message}`);
      } else if (alerts) {
        StorageService.set('price_alerts', alerts);
        itemsExported += alerts.length;
      }

      const duration = Date.now() - startTime;

      console.log(`✅ Export completed in ${duration}ms`);
      console.log(`📦 Items exported: ${itemsExported}`);

      return {
        success: errors.length === 0,
        itemsMigrated: itemsExported,
        errors,
        duration,
      };
    } catch (error) {
      console.error('❌ Export failed:', error);

      return {
        success: false,
        itemsMigrated: itemsExported,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        duration: Date.now() - startTime,
      };
    }
  }

  /**
   * Check if user has meaningful cloud data (not just empty portfolio from trigger)
   * Returns true only if user has tokens in their cloud portfolio
   */
  async hasCloudData(userId: string): Promise<boolean> {
    try {
      // Check for tokens, not portfolios (portfolios are auto-created by trigger)
      const { count } = await supabase
        .from('portfolio_tokens')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_archived', false);

      const hasTokens = (count || 0) > 0;
      console.log(`☁️ [hasCloudData] User ${userId} has ${count || 0} cloud tokens`);
      return hasTokens;
    } catch (err) {
      console.error('❌ [hasCloudData] Error:', err);
      return false;
    }
  }

  /**
   * Check if user has local data
   * Checks portfolios stored in 'portfolios' key (PortfolioManager format)
   */
  hasLocalData(): boolean {
    // IMPORTANT: Check if sync_choice_made is true - if so, any remaining data is cache, not local data
    const syncChoiceMade = StorageService.get('sync_choice_made', false);
    if (syncChoiceMade) {
      console.log('📦 [hasLocalData] sync_choice_made is true, skipping local data check');
      return false;
    }

    // Check legacy keys first
    const legacyTokens = StorageService.get('tokens', []);
    const legacyPositions = StorageService.get('positions', []);

    if (legacyTokens.length > 0 || legacyPositions.length > 0) {
      console.log(`📦 [hasLocalData] Found legacy data: ${legacyTokens.length} tokens, ${legacyPositions.length} positions`);
      return true;
    }

    // Check PortfolioManager format: portfolios are stored in 'portfolios' key
    // Each portfolio object has tokens array with positions inside
    // IMPORTANT: Only check portfolios with LOCAL format (have 'tokens' array inside portfolio object)
    // Cloud portfolios cached to localStorage have different structure (no tokens embedded)
    const portfolios = StorageService.get<any[]>('portfolios', []);
    for (const portfolio of portfolios) {
      // Skip portfolios that look like cloud format (have user_id, is_default columns)
      if (portfolio.user_id || portfolio.is_default !== undefined) {
        console.log(`📦 [hasLocalData] Skipping cloud-format portfolio: ${portfolio.name || portfolio.id}`);
        continue;
      }

      if (portfolio.tokens && portfolio.tokens.length > 0) {
        // Check if there are any positions
        for (const token of portfolio.tokens) {
          if (token.positions && token.positions.length > 0) {
            console.log(`📦 [hasLocalData] Found ${token.positions.length} positions in portfolio "${portfolio.name}" for token ${token.id}`);
            return true;
          }
        }
        // Even without positions, tokens with data counts
        console.log(`📦 [hasLocalData] Found ${portfolio.tokens.length} tokens in portfolio "${portfolio.name}"`);
        return true;
      }
    }

    console.log('📦 [hasLocalData] No local data found');
    return false;
  }

  /**
   * Compare local and cloud data to detect conflicts
   * Used when user logs in and has both local and cloud data
   */
  async compareData(userId: string): Promise<DataComparison> {
    console.log('🔍 Comparing local and cloud data...');

    // Get local data
    const localPortfolios = StorageService.get<any[]>('portfolios', []);
    const localTokens: Array<{ id: string; name: string; positionsCount: number }> = [];
    let localPositionsCount = 0;

    for (const portfolio of localPortfolios) {
      if (portfolio.tokens && Array.isArray(portfolio.tokens)) {
        for (const token of portfolio.tokens) {
          const posCount = token.positions?.length || 0;
          localTokens.push({
            id: token.id,
            name: token.displayName || token.id,
            positionsCount: posCount,
          });
          localPositionsCount += posCount;
        }
      }
    }

    // Get cloud data
    const { data: cloudTokensData } = await supabase
      .from('portfolio_tokens')
      .select('id, coin_id, symbol, display_name')
      .eq('user_id', userId)
      .eq('is_archived', false);

    const { data: cloudPositionsData } = await supabase
      .from('portfolio_positions')
      .select('id, token_id')
      .eq('user_id', userId);

    // Count positions per token
    const positionsByToken = new Map<string, number>();
    (cloudPositionsData || []).forEach(pos => {
      const count = positionsByToken.get(pos.token_id) || 0;
      positionsByToken.set(pos.token_id, count + 1);
    });

    const cloudTokens: Array<{ id: string; name: string; positionsCount: number }> = [];
    (cloudTokensData || []).forEach(token => {
      cloudTokens.push({
        id: token.coin_id,
        name: token.symbol || token.display_name || token.coin_id,
        positionsCount: positionsByToken.get(token.id) || 0,
      });
    });

    // Calculate delta
    const localTokenIds = new Set(localTokens.map(t => t.id));
    const cloudTokenIds = new Set(cloudTokens.map(t => t.id));

    const tokensOnlyLocal = [...localTokenIds].filter(id => !cloudTokenIds.has(id));
    const tokensOnlyCloud = [...cloudTokenIds].filter(id => !localTokenIds.has(id));
    const tokensInBoth = [...localTokenIds].filter(id => cloudTokenIds.has(id));

    // Count positions only in local (for tokens that exist in both)
    let positionsOnlyLocal = 0;
    let positionsOnlyCloud = 0;

    // For tokens only in local, all positions are "only local"
    for (const tokenId of tokensOnlyLocal) {
      const token = localTokens.find(t => t.id === tokenId);
      if (token) positionsOnlyLocal += token.positionsCount;
    }

    // For tokens only in cloud, all positions are "only cloud"
    for (const tokenId of tokensOnlyCloud) {
      const token = cloudTokens.find(t => t.id === tokenId);
      if (token) positionsOnlyCloud += token.positionsCount;
    }

    // Determine if there's a meaningful conflict
    const hasConflict = tokensOnlyLocal.length > 0 || positionsOnlyLocal > 0;

    const result: DataComparison = {
      hasConflict,
      local: {
        tokensCount: localTokens.length,
        positionsCount: localPositionsCount,
        tokens: localTokens,
      },
      cloud: {
        tokensCount: cloudTokens.length,
        positionsCount: cloudPositionsData?.length || 0,
        tokens: cloudTokens,
      },
      delta: {
        tokensOnlyLocal,
        tokensOnlyCloud,
        tokensInBoth,
        positionsOnlyLocal,
        positionsOnlyCloud,
      },
    };

    console.log('📊 Data comparison result:', result);
    return result;
  }

  /**
   * Merge local data into cloud (add only what's missing)
   * Unlike migrateToCloud, this doesn't replace - it adds missing items
   */
  async mergeLocalToCloud(
    userId: string,
    onProgress?: (progress: MigrationProgress) => void
  ): Promise<MigrationResult> {
    const startTime = Date.now();
    const errors: string[] = [];
    let itemsMigrated = 0;

    try {
      console.log('🔀 Starting merge (local → cloud)...');

      if (onProgress) {
        onProgress({ stage: 'Analyse des données...', current: 1, total: 4, percentage: 25 });
      }

      // Get comparison
      const comparison = await this.compareData(userId);

      // Get default portfolio
      const { data: portfolios } = await supabase
        .from('portfolios')
        .select('id')
        .eq('user_id', userId)
        .eq('is_default', true)
        .single();

      if (!portfolios) {
        throw new Error('No default portfolio found');
      }

      const defaultPortfolioId = portfolios.id;

      if (onProgress) {
        onProgress({ stage: 'Fusion des tokens...', current: 2, total: 4, percentage: 50 });
      }

      // Add tokens that are only in local
      const localPortfolios = StorageService.get<any[]>('portfolios', []);
      const tokensToAdd = comparison.delta.tokensOnlyLocal;

      for (const tokenId of tokensToAdd) {
        // Find token in local data
        let localToken: any = null;
        for (const portfolio of localPortfolios) {
          if (portfolio.tokens) {
            localToken = portfolio.tokens.find((t: any) => t.id === tokenId);
            if (localToken) break;
          }
        }

        if (localToken) {
          // Add token to cloud
          const { data: newToken, error: tokenError } = await supabase
            .from('portfolio_tokens')
            .insert({
              user_id: userId,
              portfolio_id: defaultPortfolioId,
              coin_id: localToken.id,
              symbol: localToken.displayName || localToken.id.toUpperCase(),
              display_name: localToken.displayName || localToken.id,
            })
            .select()
            .single();

          if (tokenError) {
            errors.push(`Token ${tokenId}: ${tokenError.message}`);
            continue;
          }

          itemsMigrated++;

          // Add positions for this token
          if (localToken.positions && localToken.positions.length > 0) {
            const positionsToInsert = localToken.positions.map((p: any) => ({
              user_id: userId,
              portfolio_id: defaultPortfolioId,
              token_id: newToken.id,
              type: p.type,
              date: p.date,
              quantity: p.quantity?.toString() || '0',
              price: p.price?.toString() || '0',
              note: p.note || null,
              source: 'merge',
            }));

            const { error: posError } = await supabase
              .from('portfolio_positions')
              .insert(positionsToInsert);

            if (posError) {
              errors.push(`Positions for ${tokenId}: ${posError.message}`);
            } else {
              itemsMigrated += positionsToInsert.length;
            }
          }
        }
      }

      if (onProgress) {
        onProgress({ stage: 'Nettoyage...', current: 3, total: 4, percentage: 75 });
      }

      // Only clear local data if there were NO errors
      if (errors.length === 0) {
        console.log('✅ No errors during merge, clearing local data...');
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          // Be specific about what we remove - only portfolio data, not caches
          if (key && (
            key === 'portfolios' ||
            key === 'tokens' ||
            key === 'positions' ||
            key === 'active_portfolio_id'
          )) {
            keysToRemove.push(key);
          }
        }
        console.log('🗑️ Removing keys:', keysToRemove);
        keysToRemove.forEach(key => localStorage.removeItem(key));
      } else {
        console.warn('⚠️ Errors during merge, keeping local data as backup:', errors);
      }

      if (onProgress) {
        onProgress({ stage: 'Fusion terminée!', current: 4, total: 4, percentage: 100 });
      }

      return {
        success: errors.length === 0,
        itemsMigrated,
        errors,
        duration: Date.now() - startTime,
      };
    } catch (error) {
      console.error('❌ Merge failed:', error);
      return {
        success: false,
        itemsMigrated,
        errors: [error instanceof Error ? error.message : 'Unknown error'],
        duration: Date.now() - startTime,
      };
    }
  }

  /**
   * Resolve conflicts between local and cloud data
   *
   * Strategy options:
   * - cloud_wins: Keep cloud data, discard local
   * - local_wins: Replace cloud with local data
   * - merge: Add missing local data to cloud
   */
  async resolveConflicts(
    userId: string,
    strategy: 'cloud_wins' | 'local_wins' | 'merge' = 'merge'
  ): Promise<MigrationResult> {
    console.log(`🔄 Resolving conflicts (strategy: ${strategy})...`);

    if (strategy === 'cloud_wins') {
      // Keep cloud, clear local
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('portfolio_') ||
          key === 'portfolios' ||
          key === 'tokens' ||
          key === 'positions' ||
          key === 'active_portfolio_id'
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      return {
        success: true,
        itemsMigrated: 0,
        errors: [],
        duration: 0,
      };
    } else if (strategy === 'local_wins') {
      // Replace cloud with local
      return await this.migrateToCloud(userId);
    } else {
      // Merge: add missing local to cloud
      return await this.mergeLocalToCloud(userId);
    }
  }
}

export const migrationService = new MigrationService();
